<template>
    <WidgetSection
        ref="WidgetSection"
        :loaded="loaded"
        :loading="loading"
        v-if="!hide"
        :defaultToggle="defaultToggle"
        @loading-start="handleLoadingStart"
        loadingText="common.loading_1"
        :paywall="paywall"
        :cssClass="cssClassWidgetSection"
    >
        <template v-slot:section-title>
            <div class="title">
                {{ $t('components.prospect_same_address.name')}}
            </div>
        </template>

        <template v-slot:section-content>
            <div class="row row-content">
                <div class="table-container">
                    <table class="table-type-1 companies">
                        <thead>
                            <tr class="table-header">
                                <td></td>
                                <td>{{ $t('common.company_name') }}</td>
                                <td>{{ $t('common.coc') }}</td>
                                <td>{{ $t('common.phone') }}</td>
                                <td>{{ $t('common.website') }}</td>
                                <td>{{ $t('common.address') }}</td>
                                <td>{{ $t('common.postcode') }}</td>
                                <td>{{ $t('common.city') }}</td>
                            </tr>
                        </thead>
                        <tbody>
                            <tr class="company clickable" v-for="company in sameAddress.details" :key="company.id" @click="openCompanyProfile(company)">
                                <td>
                                    <div class="logo-wrp">
                                        <CompanyLogo
                                            :domain="company.domain ? company.domain : null"
                                            :company_name="company.name ? company.name : company.domain ? company.domain : null"
                                            class="company-image"
                                        />
                                        <LabelMini 
                                            :label="company.label"
                                            v-if="company.label === 0 || company.label === 1"
                                        />
                                    </div>
                                </td>
                                <td class="company-name">{{ company.name }}</td>
                                <td>{{ company.coc || company.id }}</td>
                                <td>{{ company.phone_international || '-' }}</td>
                                <td class="website">{{ company.url || '-' }}</td>
                                <td class="address">{{ company.address || '-' }}</td>
                                <td>{{ company.postcode || '-' }}</td>
                                <td>{{ company.city || '-' }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </template>
    </WidgetSection>
</template>

<script>
    import { useProspectStore } from '@/stores/prospect';
    import { useAccountStore } from '@/stores/account';
    import { useUserStore } from '@/stores/user';
    import { fetchUrl } from '@/services/ppApi';
    import WidgetSection from '@/components/sections/WidgetSection';
    import CompanyLogo from '@/components/elements/misc/CompanyLogo';
    import LabelMini from '@/components/elements/misc/LabelMini';
    import i18n from "@/plugins/i18n";
    import { useToastStore } from '@/stores/utils/toast';

    export default {
        name: "ProspectSameAddress",
        components: {
            WidgetSection,
            CompanyLogo,
            LabelMini
        },
        props: {
            auto: {
                type: Boolean,
                default: false
            },
            defaultToggle: {
                type: Boolean,
                default: false
            },
            paywall: {
                type: Boolean,
                default: false
            },
            cssClassWidgetSection: {
                type: String,
                required: false
            },
            showLoadingToasts: {
                type: Boolean,
                default: true
            }
        },
        data() {
            return {
                loading: false,
                loaded: false,
                abortController: null,
                timeoutId: null,
                isActive: true
            }
        },
        computed: {
            paywallActive() {
                return this.paywall && useAccountStore().paywall;
            },
            hide() {
                return !this.sameAddress || (this.loaded && (!this.sameAddress.details || this.sameAddress.details.length < 1));
            },
            sameAddress() {
                return useProspectStore().sameAddress;
            },
        },
        methods: {
            async fetchSameAddress() {
                if (!this.sameAddress || (this.sameAddress.api_urls && this.sameAddress.api_urls.length < 1) || this.loaded) {
                    return;
                }

                if (this.abortController) {
                    this.abortController.abort();
                    this.abortController = null;
                }

                this.abortController = new AbortController();
                this.loading = true;

                try {
                    const response = await fetchUrl(this.sameAddress.api_urls[0], this.abortController.signal);
                    this.sameAddress.details = response.data.companies;

                    if(this.showLoadingToasts && this.isActive) {
                        if(this.sameAddress?.details?.length > 0) {
                            useToastStore().addToast(i18n.global.t(`toasts.success.prospect_same_address`), 'success');
                        } else {
                            useToastStore().addToast(i18n.global.t(`toasts.error.prospect_same_address`), 'error'); 
                        }
                    }
                } catch (error) {
                    if (error?.code !== 'ERR_CANCELED') {
                        this.sameAddress?.details ? this.sameAddress.details = null : '';
                    }
                } finally {
                    this.loading = false;
                    this.loaded = true;
                    this.abortController = null;
                }
            },
            async handleLoadingStart() {
                if (!this.paywallActive && !this.loaded) {
                    await this.fetchSameAddress();
                    
                    if(!this.hide && this.$refs.WidgetSection) {
                        this.$refs.WidgetSection.toggleSection();
                    }
                }
            },
            openCompanyProfile(company) {
                if(company) {
                    const isAuthenticated = useUserStore().isAuthenticated;
                    const id = (!isAuthenticated && company.ch) ? (company.id + '_' + company.ch) : company.id;

                    if(process.env.VUE_APP_TYPE === 'search') {
                        this.$router.push({ name: 'search-result', params: { id } });
                    } else {
                        const routeData = this.$router.resolve({ name: 'company', params: { id }});
                        window.open(routeData.href, '_blank');
                    }
                }
            },
        },
        mounted() {
            if(!this.paywallActive && !this.loaded && (this.auto || this.defaultToggle)) {
                this.loading = true;
                this.timeoutId = setTimeout(() => {
                    this.fetchSameAddress();
                }, 300);
            }
        },
        unmounted() {
            this.isActive = false;

            if (this.abortController) {
                this.abortController.abort();
                this.abortController = null;
            }
            if (this.timeoutId) {
                clearTimeout(this.timeoutId);
            }
        }
    }
</script>